import { useEffect, useRef, useState } from "react";
import { webinarRepository } from "./repository";
import { isAdminError } from "./types";

interface RepositoryData<T> {
  data?: T;
  error?: unknown;
  loading: boolean;
}

/**
 * Loads data through the repository and reloads it whenever the data changes, including
 * changes made in another tab (e.g. a registration made while the admin dashboard is open).
 * `key` should change whenever `load` would return something different (e.g. a slug or filters).
 */
export function useRepositoryData<T>(load: () => Promise<T>, key: string): RepositoryData<T> {
  const [state, setState] = useState<RepositoryData<T>>({ loading: true });
  const [version, setVersion] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => webinarRepository.subscribe(() => setVersion(v => v + 1)), []);

  useEffect(() => {
    let cancelled = false;
    setState(prev => ({ ...prev, loading: true }));
    loadRef.current().then(
      data => { if (!cancelled) setState({ data, loading: false }); },
      error => {
        // Signing out reloads open admin data a moment before the sign-in screen replaces it
        if (!(isAdminError(error) && error.code === "not_signed_in")) console.error(error);
        if (!cancelled) setState({ error, loading: false });
      },
    );
    return () => { cancelled = true; };
  }, [key, version]);

  return state;
}
